import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { FreewheelModel } from 'tag/ts/tag/model/ads/freewheel/FreewheelModel';
import { AbstractAssetPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/AbstractAssetPlaybackUtility';
import { FWUtilities } from 'tag/ts/tag/utilities/FWUtilities';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description PID content resolution utility for VTG resource configuration
 *
 * **PID Content Resolution:**
 * Handles the resolution of Paramount ID (PID) content identifiers into playable
 * streaming URLs and associated metadata. PIDs reference content in MPX with
 * complete streaming configuration and business logic.
 *
 * **Content Resolution Strategy:**
 * - **Primary Source**: Uses Millstone streamingUrl when available for optimized delivery
 * - **Fallback Source**: Falls back to apiMetadata streamingUrl for content availability
 * - **Ad Integration**: Applies Freewheel ad configuration based on content and user context
 * - **Geographic Handling**: Adjusts content resolution for international vs domestic markets
 *
 * **Business Logic Integration:**
 * - **Video Ad Enablement**: Checks user subscription and content policies for ad insertion
 * - **International Content**: Handles geographic content licensing and ad-free tiers
 * - **Freewheel Integration**: Coordinates with Freewheel ad system for monetization
 * - **MPX Content Metadata**: Resolves content information from Paramount's MPX system
 *
 * **Resource Builder Integration:**
 * Used by DesktopPIDResourceConfigBuilder and HTML5PIDResourceConfigBuilder to
 * resolve PID-based content into VTG-compatible resource configurations.
 *
 * **Business Impact:**
 * Essential for PID-based content playback, ad monetization decisions, and
 * international content licensing compliance across the platform.
 */
export class PIDPlaybackUtility extends AbstractAssetPlaybackUtility {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'PIDPlaybackUtility',
				...argument
			}
		);
	}

	/**
	 * @description Resolves PID content into streaming URL with fallback strategy and ad integration
	 *
	 * **Content Resolution Pipeline:**
	 * Implements multi-source content resolution that prioritizes optimized delivery
	 * while ensuring content availability through fallback mechanisms.
	 *
	 * **Resolution Strategy:**
	 * 1. **Primary Source**: Millstone streamingUrl for optimized content delivery
	 * 2. **Fallback Source**: apiMetadata streamingUrl for content availability guarantee
	 * 3. **Ad Integration**: Applies Freewheel configuration based on content and user context
	 * 4. **Geographic Handling**: Adjusts resolution for international vs domestic markets
	 *
	 * **Business Logic Applied:**
	 * - **Video Ad Enablement**: Checks subscription tier and content policies
	 * - **International Markets**: Handles geographic content licensing restrictions
	 * - **Freewheel Integration**: Coordinates ad insertion with content delivery
	 * - **Content Availability**: Ensures playback even with partial configuration
	 *
	 * **Error Handling:**
	 * - **Source Fallback**: Graceful degradation from primary to fallback sources
	 * - **Configuration Validation**: Ensures minimum viable streaming configuration
	 * - **Ad Integration Errors**: Continues content delivery even if ad setup fails
	 *
	 * @returns {string} Resolved streaming URL for VTG player resource configuration
	 */
	getMediaUrl(): string {
		const fwModel = new FreewheelModel( { tag: this.tag } );
		const fwUtil = FWUtilities.getInstance();
		const isIntl = this.isIntl();
		const videoAdsEnabled = this?.videoAdsEnabled();
		let mediaUrl: string;
		const millstoneStreamingUrl = this.tag?.millstoneUtilities?.getStreamingUrl();
		const apiMetadataStreamingUrl = this?.apiMetadata?.streamingUrl;

		if ( this.utils?.URL?.valid( millstoneStreamingUrl ) ) {
			mediaUrl = millstoneStreamingUrl;
			this.logger?.log( 'getMediaUrl:', 'using streamingUrl from millstone:', mediaUrl );
		} else {
			mediaUrl = apiMetadataStreamingUrl;
			this.logger?.log( 'getMediaUrl:', 'millstone streamingUrl is invalid, fallback to apiMetadata streamingUrl:', mediaUrl );
		}

		if ( !isIntl ) {
			this.logger?.log( 'getMediaUrl:', 'is not INTL... will not add Freewheel parameters to mediaUrl' );
		}
		if ( !videoAdsEnabled ) {
			this.logger?.log( 'getMediaUrl:', 'video ads disabled... will not add Freewheel parameters to mediaUrl' );
		}
		// append FW parameters to url as query parameters when videoAds is enabled in INTL context
		if ( isIntl && videoAdsEnabled ) {
			mediaUrl = fwUtil.createFWUrl( fwModel, mediaUrl );
			this.logger?.log( 'getMediaUrl:', 'is INTL, video ads enabled... append Freewheel parameters to mediaUrl as query parameters:', mediaUrl );
		}

		this?.logger?.log( 'getMediaUrl:', 'returning url:', mediaUrl );
		return mediaUrl;
	}
}